<script lang="ts">
import {Component, Prop, toNative} from 'vue-facing-decorator'
import {asArray} from '@/utils/arrayUtils'
import {shownMarkdown} from '@/utils/markdownMentions'
import {fieldVisible} from '@/types/Idea'
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import {InstanceContext} from "@/types/InstanceContext";
import Instance, {emptyInstance} from "@/types/Instance";
import IdeaProperty from "@/types/IdeaProperty";
import {takeHandover} from '@/utils/handover';
import {IN_PLACE, instanceViewKey} from '@/utils/instanceLink';
import {editingFrom, isPreview, Preview, previewFromRow, VIEW_READ, ViewRead} from '@/utils/instancePreview';
import DialogLoading from "@/components/shared/DialogLoading.vue";
//import Instance from "@/types/Instance";
import PropertyValue from "@/components/live/instance/PropertyValue.vue";
import DeleteInstanceDialog from "@/components/live/instance/DeleteInstanceDialog.vue";
import StateDialog from "@/components/live/instance/StateDialog.vue";
import EditInstanceService from "@/services/EditInstanceService";
import {isMediaType} from "@/utils/mediaTypes";
import ImagePreviewDialog from "@/components/shared/ImagePreviewDialog.vue";
import ViewInfo from "@/components/shared/ViewInfo.vue";
import TabbedSection from "@/components/shared/TabbedSection.vue";
import InstanceFields from "@/components/live/instance/InstanceFields.vue";
import FieldFailures from "@/components/live/instance/FieldFailures.vue";
import {failuresAt, FieldFailure} from "@/utils/fieldValidation";
import {InstanceSection, instanceSections, OWN_TAB} from "@/utils/instanceSections";
import {workspaceNames} from "@/utils/workspaceNames";
import {ImagePreview} from "@/types/ImagePreview";
import {instanceEdit, instanceView} from "@/utils/heading";
import {hiddenCount, instanceColorOf, personName, personOf} from "@/utils/propertyDisplay";
import {exactTime, relativeTime} from "@/utils/relativeTime";
import {WorkspacePerson} from "@/types/Person";
import HiddenCount from "@/components/shared/HiddenCount.vue";
import UserAvatar from "@/components/shared/UserAvatar.vue";
import StateSeal from "@/components/live/instance/StateSeal.vue";
import {hasCompositeIdentifier} from "@/utils/ontosUtils";
import {refuse} from "@/utils/refusal";
import ColorAccent from "@/components/shared/ColorAccent.vue";
import InstanceListRow, {RowField, rowFieldsOf} from "@/components/live/instance/InstanceListRow.vue";
import {ColoredOption} from "@/types/ColoredOption";
import ColoredOptionChip from "@/components/shared/ColoredOptionChip.vue";
import TableFit from "@/components/shared/TableFit.vue";
import ShowClosedToggle from "@/components/shared/ShowClosedToggle.vue";
import MarkdownViewer from "@/components/shared/markdown/MarkdownViewer.vue";
import RelatedItemLine from "@/components/live/instance/RelatedItemLine.vue";
import InstanceCard from "@/components/shared/InstanceCard.vue";
import InstanceIdentifier from "@/components/shared/InstanceIdentifier.vue";
import {hasRelationshipKanban, relationshipEditing, relationshipScope, withRelated} from "@/utils/relationshipKanban";
import RelationshipUnlinkDialog from "@/components/live/instance/RelationshipUnlinkDialog.vue";
import {vSmoothHeight} from "@/utils/motion";
import {knownForCreate, offerKnown} from "@/utils/instanceCreation";
import RelationshipBoard from "@/components/live/instance/RelationshipBoard.vue";
import RelationshipAdd from "@/components/live/instance/RelationshipAdd.vue";
import ViewTabs, {ViewTab} from "@/components/shared/ViewTabs.vue";
import {RelationshipView} from "@/storage";

@Component({emits: ['open-instance', 'edit', 'deleted', 'changed', 'not-found', 'crumb'], 
  name: 'ontos-instance-view',
  components: {
    'ontos-color-accent': ColorAccent,
    'ontos-instance-card': InstanceCard,
    'ontos-instance-identifier': InstanceIdentifier,
    'ontos-instance-list-row': InstanceListRow,
    'ontos-related-item-line': RelatedItemLine,
    'markdown-viewer': MarkdownViewer,
    'ontos-colored-option': ColoredOptionChip,
    'ontos-table-fit': TableFit,
    'ontos-show-closed-toggle': ShowClosedToggle,
    'ontos-property-value': PropertyValue,
    'ontos-hidden-count': HiddenCount,
    'ontos-user-avatar': UserAvatar,
    'ontos-state-seal': StateSeal,
    'ontos-view-info': ViewInfo,
    'ontos-tabbed-section': TabbedSection,
    'ontos-instance-fields': InstanceFields,
    'ontos-field-failures': FieldFailures,
    'ontos-relationship-board': RelationshipBoard,
    'ontos-relationship-add': RelationshipAdd,
    'ontos-view-tabs': ViewTabs,
    'ontos-dialog-loading': DialogLoading,
    'ontos-delete-instance-dialog': DeleteInstanceDialog,
    'ontos-relationship-unlink-dialog': RelationshipUnlinkDialog,
    'ontos-state-dialog': StateDialog,
    'ontos-image-preview-dialog': ImagePreviewDialog
  },
  directives: {'smooth-height': vSmoothHeight}
})
class InstanceView extends IdeaLevelRouteComponent {
  //exposta ao template: a cor de um item é função de módulo, e a régua da linha a chama de lá
  instanceColorOf = instanceColorOf
  hiddenCount = hiddenCount

  /**
   * O rodapé da ficha: quem criou e quem alterou por último, e há quanto tempo — as pessoas achadas
   * nas do ambiente pelo id que o servidor carimbou. Embaixo e miúdo (pedido do usuário): é o que se
   * consulta de vez em quando, e no alto disputava a vista com o conteúdo.
   *
   * Só o que há: item de antes da autoria tem a data e não a pessoa. "Alterado" só quando houve
   * alteração depois de criar — a criação carimba as duas datas iguais, e diria duas vezes o mesmo.
   */
  get authorship(): Array<{ key: string, label: string, person?: WorkspacePerson, when?: string, exact?: string }> {
    const {_createdBy, _updatedBy, _createdAt, _updatedAt} = this.instance
    const creator = personOf(_createdBy, this.workspace.people)
    const editor = personOf(_updatedBy, this.workspace.people)
    const changed = _updatedAt ? _updatedAt !== _createdAt : !!editor && editor !== creator
    const entry = (key: 'created' | 'updated', person: WorkspacePerson | undefined, at: string | undefined) => ({
      key,
      label: this.$tt(person ? (key === 'created' ? 'created-by' : 'updated-by') : key) as string,
      person,
      when: at ? relativeTime(at) : undefined,
      exact: at ? exactTime(at) : undefined
    })
    return [entry('created', creator, _createdAt), ...(changed ? [entry('updated', editor, _updatedAt)] : [])]
        .filter(({person, when}) => person || when)
  }

  //exposto ao template: função de módulo não é enxergada de lá
  personName = personName
  //idem: o markdown da aba dele, como se mostra — ver `markdownMentions`
  shownMarkdown = shownMarkdown
  //idem: o predicado da chave composta decide a coluna do identificador
  hasCompositeIdentifier = hasCompositeIdentifier

  //exposto ao template: o predicado é função de módulo, não é enxergado de lá
  isMediaType = isMediaType

  // De onde vêm system/idea/instance. Nunca ler $route aqui: este componente é
  // usado tanto pela página de rota quanto por modais que mostram outra instância.
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  /**
   * Desenha a ficha dentro do card do item. A ROTA pede; o modal não — ver o template.
   *
   * Quem hospeda decide, e não esta tela: ela é a mesma nos dois lugares, e é a moldura em volta
   * que muda.
   */
  @Prop({type: Boolean, default: false}) readonly framed!: boolean
  /**
   * O que mostrar antes da leitura completa, quando quem hospeda já tem o item (o modal aberto pela
   * lista, ou depois de salvar). Na rota a mesma prévia vem pela entrega — ver o `load()`.
   */
  @Prop({type: Object, default: null}) readonly preview!: Preview | null

  instance: Instance = emptyInstance()
  /** Mostrando a prévia, e a leitura completa ainda não chegou. */
  provisional = false
  /** Os relacionamentos da prévia que vieram só resumidos: a seção espera a leitura completa. */
  pending: string[] = []

  /**
   * A leitura completa já está na tela. Antes dela, o que não vem na prévia ou pode estar velho espera:
   * as ações (a edição receberia o item parcial), o rodapé de autoria e as falhas, e as seções de
   * relacionamento resumidas.
   */
  get complete(): boolean {
    return !this.provisional
  }

  /** Esta seção depende de um relacionamento que a prévia trouxe só resumido? A raiz nunca. */
  isPending(section: InstanceSection): boolean {
    return this.provisional && !!section.key && this.pending.includes(section.key.split('.')[0])
  }

  //todo baixar só as necessárias
  //ideas: []
  isShowClosedItems: { [prop: string]: boolean } = {}
  isOnDelete = false
  /** O modal de escolher a situação está aberto */
  isOnStateChange = false
  relDomains = {}
  reverseInfo: any = null
  /**
   * A aba de cada bloco de relacionamento, pela chave da seção.
   *
   * Por SEÇÃO como o `isShowClosedItems`, porque o mesmo relacionamento pode aparecer em
   * caminhos diferentes; mas LEMBRADA por propriedade, que é o recorte em que a escolha faz
   * sentido — ver o `onViewChange`.
   */
  sectionViews: { [key: string]: RelationshipView } = {}
  /** Algum card se moveu no quadro embutido desde a última leitura — ver o onBoardChanged. */
  boardChanged = false
  //Guardado na tela e não na célula: o que está ampliado não pode mudar quando a
  //tabela de relacionamento é recarregada
  imagePreview: ImagePreview | null = null

  //Sobrescreve o getter da base, que leria $route.params.idea
  get ideaAlias() {
    return this.context.idea
  }

  /** O assunto da tela. A regra mora no utils/heading, com a da edição. */
  get heading() {
    //O id vem do contexto: a resposta desta tela não pede `meta: ["instance"]`, e é o contexto
    //que sabe qual item está aberto — é dele que sai a cor do acento.
    return instanceView(this.instance, this.idea, this.context.instance)
  }

  /**
   * O cabeçalho da janela que MUDA alguma coisa deste item — hoje, a da situação.
   *
   * O mesmo do formulário, e não o da ficha: a ficha mostra, e ali "Demanda" embaixo do nome diz
   * de que coleção o item é; a janela da situação muda, e "Editar Demanda" é o que o app diz
   * quando se está mudando. O quadro usa este mesmo, pelo mesmo motivo.
   */
  get editHeading() {
    return instanceEdit(this.instance, this.idea, this.context.instance)
  }

  /**
   * Os candidatos que cada quadro de relacionamento já leu, por COLEÇÃO mostrada.
   *
   * Mora aqui porque a ficha é quem sobrevive à troca de aba: o quadro desmonta ao virar lista
   * e remonta ao voltar, e sem este lugar cada ida lia os candidatos outra vez — a requisição
   * que aparecia no servidor ao alternar Lista ↔ Kanban dentro da ficha.
   *
   * Pela coleção, e não pela seção: duas seções podem mostrar a MESMA coleção por caminhos
   * diferentes, e os candidatos dela são os mesmos nas duas.
   */
  sectionCandidates: { [ideaAlias: string]: any } = {}

  candidatesOf(section: InstanceSection): any {
    const alias = section.property && section.property.relationship?.idea
    return alias ? this.sectionCandidates[alias] || null : null
  }

  rememberCandidates(section: InstanceSection, read: any) {
    const alias = section.property && section.property.relationship?.idea
    //Objeto novo, e não chave mutada: o mapa é lido do template, e trocar a referência é o que
    //não depende de ele ser reativo por proxy.
    if (alias) this.sectionCandidates = {...this.sectionCandidates, [alias]: read}
  }

  /**
   * Cada host decide o que fazer: a página abre modal, o modal troca a ficha.
   *
   * O `over` diz que o pedido nasceu DENTRO de um diálogo desta ficha — o card do
   * Kanban de relacionamento, o link de quem impede a exclusão. Esses o modal empilha,
   * para que fechar a ficha nova devolva o diálogo de onde se clicou; um link da
   * própria ficha é navegação, e navegação não empilha janela.
   */
  openInstance(context: InstanceContext, over = false, preview?: Preview) {
    this.$emit('open-instance', context, over, preview)
  }

  /** O card do quadro de um relacionamento, por cima desta janela, com a prévia que o quadro montou. */
  openBoardItem(context: InstanceContext, preview: Preview | null) {
    this.openInstance(context, true, preview || undefined)
  }

  /** O item de uma linha de tabela já tem o corpo (lido com `relProperties`): a ficha dele abre com ele. */
  /**
   * O contexto de uma linha de relacionados: o do item da linha, e não o da ficha — com o da ficha, o
   * `{{campo}}` de um markdown da linha procurava o campo na coleção errada.
   */
  rowContext(section: InstanceSection, item: Instance): InstanceContext {
    return {system: this.context.system, idea: section.idea.alias, instance: item._instance}
  }

  openRow(section: InstanceSection, item: Instance) {
    this.openInstance(this.rowContext(section, item), true, previewFromRow(item, section.idea))
  }

  /**
   * O host decide o que fazer depois: a página navega para a listagem, o modal
   * fecha e manda o pai recarregar.
   */
  async onDeleteConfirmed() {
    try {
      await this.saving(async () => {
        await new EditInstanceService(this.context, this.$instances, this.idea).delete(Number(this.context.instance))
      })
    } catch (err) {
      //O servidor refaz a checagem; se recusou aqui, a prévia estava defasada
      this.$emit('showErrorMessage', err.message)
      return
    }
    this.isOnDelete = false
    this.info(this.$tt('info.delete-item', {name: this.instance._identifier}))
    this.$emit('deleted')
  }


  propertyValueArrayGeneric(instance: Instance, property: IdeaProperty) {
    return asArray(this.propertyValueGeneric(instance, property))
  }

  propertyValueArray(property: IdeaProperty) {
    return asArray(this.propertyValue(property))
  }


  propertyValue(property: IdeaProperty) {
    if (property.relationship) return this.instance._rels[property.alias]
    return this.instance[property.alias]
  }

  propertyValueGeneric(instance: Instance, property: IdeaProperty) {
    if (property.relationship) return instance._rels[property.alias]
    return instance[property.alias]
  }

  /**
   * Os itens de uma seção-tabela, lidos do DONO da lista e não da instância da tela.
   *
   * Numa tabela aninhada — "Vendedor · Regiões" — o dono é o vendedor, não o pedido
   * que a ficha mostra. Ler de `this.instance` devolvia lista vazia nesses casos.
   */
  itemsOf(section: InstanceSection): Instance[] {
    const owner = section.instance
    return asArray(owner && owner._rels && owner._rels[section.property!.alias])
  }

  relationshipIdea(property: IdeaProperty) {
    //Sem o `!`: a asserção era mentira para propriedade que não é relacionamento, e
    //foi o que deixou um acesso inseguro passar pelo compilador.
    if (!property.relationship) return undefined
    return this.workspace.ideaWith(property.relationship.idea)
  }

  relationshipWorkflow(property: IdeaProperty) {
    const workflowAlias = this.relationshipIdea(property)?.workflow
    if (workflowAlias) return this.workspace.workflowWith(workflowAlias)
    return undefined
  }

  /** O que vai embaixo do nome de um item da seção, na visão compacta — ver `rowFieldsOf`. */
  relRowFields(section: InstanceSection, item: Instance): RowField[] {
    return rowFieldsOf(this.relProperties(section) || [], property => this.propertyValueArrayGeneric(item, property))
  }

  /**
   * A situação do item da seção, na coluna da tabela e na linha da visão compacta; sem fluxo, nada. A
   * regra de "sem situação é a inicial" mora no fluxo (`coloredStateOf`).
   */
  relRowState(section: InstanceSection, item: Instance): ColoredOption | null {
    const workflow = this.relationshipWorkflow(section.property!)
    return workflow ? workflow.coloredStateOf(item) : null
  }


  /** Fecha o modal ANTES de gravar: a situação escolhida já não depende mais dele. */
  async onPickState(state: any) {
    this.isOnStateChange = false
    await this.changeTo(state.id)
  }

  /**
   * Muda a situação. O servidor confere as regras que leem a situação ("informe o prazo antes de
   * concluir"), e a recusa vai para a janela de recusa, como a do Kanban — sem isto ela escapava do
   * clique como erro solto. Com o "Editar", que troca a ficha pela edição, como o lápis dela.
   */
  async changeTo(stateId: number) {
    const {system: systemAlias, idea: ideaAlias, instance: instanceId} = this.context
    try {
      await this.$instances.updateState({systemAlias, ideaAlias, instanceId: Number(instanceId)}, stateId)
    } catch (err) {
      refuse(err, this.idea, {edit: () => this.onEdit()})
      return
    }
    this.instance._state = stateId
    this.info(this.$tt('info.value-changed', {
      property: this.$tt('state'), value: this.workflow!.stateOf(stateId)!.name
    }))
    this.$emit('changed')
    await this.recompute()
  }

  /**
   * Depois de mudar a situação pelo selo, o que o servidor recalcula — a calculada que lê `_state`, o
   * nome do item que ela compõe — vem da releitura da ficha. A mudança só sabe da situação, e o resto
   * ficava velho até recarregar: o mesmo defeito do arrasto no Kanban (ver `KanbanBoard.recompute`).
   * Só na coleção com calculada; falhando, a ficha fica como estava, e a situação já foi gravada.
   */
  private async recompute() {
    if (!this.idea.properties.some(property => property.formula)) return
    try {
      this.apply(await this.readFromServer())
    } catch (err) {
      console.warn('Não foi possível reler a ficha depois de mudar a situação', err)
    }
  }

  /**
   * A ficha, em até dois tempos.
   *
   * Entregue pronta — o Expandir da ficha em modal —, é a leitura completa e basta. Entregue como
   * PRÉVIA (`utils/instancePreview`: o item de uma lista, o que a edição guardou), a ficha a mostra já
   * — `loaded` na hora — e relê por trás, e a barra de progresso fica até a leitura chegar. Sem nada,
   * lê, como sempre: URL direta, F5, voltar. Um caminho só: o que muda é de onde vem a primeira leitura.
   */
  async load() {
    try {
      const given: ViewRead | null = this.preview || takeHandover(instanceViewKey(this.context))
      if (given) {
        this.apply(given)
        if (!isPreview(given)) return
        this.provisional = true
        this.pending = given.pending
        this.loaded = true
        this.$emit('loaded')
      }
      this.apply(await this.readFromServer())
      this.provisional = false
      this.pending = []
    } catch (err) {
      //Quem hospeda decide o que fazer: a página vai para home, o modal fecha
      if (err.response && err.response.status === 404) this.$emit('not-found')
      else this.throwError(this.$tt("error.view-item", {params: JSON.stringify(this.context)}), err)
    }
  }

  private readFromServer(): Promise<ViewRead> {
    const {system: systemAlias, idea: ideaAlias, instance: instanceId} = this.context
    return this.$instances.get({systemAlias, ideaAlias, instanceId: Number(instanceId)}, VIEW_READ)
  }

  /** Põe na tela uma leitura — a entregue, a prévia ou a do servidor: a forma é a mesma. */
  private apply(res: ViewRead) {
    const {system: systemAlias, idea: ideaAlias, instance: instanceId} = this.context
    if (this.idea.workflow && !res.value._state) {
      res.value._state = this.workflow?.initialState.id;
    }
    this.instance = res.value
    //O nome do item para o último degrau do caminho. Vai por EVENTO, e não por um
    //estado compartilhado, porque esta tela é usada pela página E pelo modal: quem
    //hospeda decide se aquilo é o endereço da janela. A página repassa; o modal não
    //escuta, e por isso abrir uma ficha relacionada não reescreve o caminho de baixo.
    //A identidade vai junto do texto — o App a compara com a rota (ver instanceLabel
    //no App.vue) para nunca aceitar o rótulo de uma tela anterior que demorou a
    //responder.
    this.$emit('crumb', {
      system: systemAlias, idea: ideaAlias, instance: instanceId, text: this.heading.title
    })
    this.relDomains = res.relDomains ?? {}
    this.reverseInfo = res.reverseInfo

    //Por SEÇÃO, e não por propriedade: o mesmo relacionamento pode aparecer em
    //caminhos diferentes ("Vendedor · Regiões" e "Regiões"), e a caixa de cada um é
    //independente. A chave da seção é o caminho em alias, que já distingue os dois.
    this.sections.filter(section => section.table)
        .forEach(section => this.isShowClosedItems[section.key] = false)

    //A aba lembrada de cada bloco. Depois das seções existirem, e só onde há quadro: pedir
    //ao storage por um relacionamento que não tem Kanban guardaria uma escolha impossível.
    this.sections.filter(section => this.hasKanban(section))
        .forEach(section => {
          const stored = this.$storage.relationshipView(this.context.system, this.idea.alias,
              relationshipScope(this.idea.alias, section.property!.alias))
          if (stored) this.sectionViews[section.key] = stored
        })
    //As calculadas — do item e dos relacionados das seções — já vêm prontas do servidor, que
    //é quem calcula. A ficha as calculava aqui, executando o `script` do designer.
  }

  asArray(elements: any) {
    return asArray(elements)
  }

  /**
   * O que o item aberto viola NESTE relacionamento — a regra da lista ("no máximo 3 canções"). O
   * múltiplo vira seção, e não campo: sem isto a marca, que mora no campo, não aparecia na ficha, só
   * na listagem. Do item aberto, e não do relacionado.
   */
  sectionFailures(section: InstanceSection): FieldFailure[] {
    if (!this.complete) return []
    //O markdown que virou seção leva as falhas do campo junto, do item de que ele é — como faria
    //no bloco de campos
    if (section.markdown) return failuresAt(section.instance, section.markdown.alias)
    return section.property ? failuresAt(this.instance, section.property.alias) : []
  }

  hasKanban(section: InstanceSection): boolean {
    //A seção da própria instância não tem relacionamento para abrir
    if (!section.property) return false
    return hasRelationshipKanban(section.property, this.idea.alias,
        section.idea, this.workspace.ideas, this.itemsOf(section).length)
  }

  /** As abas da ficha: uma por seção, com o nome dela (ver `instanceSections`). */
  /**
   * O id do lugar dos botões do quadro na linha das abas (ver o `header-to` do quadro). Único por
   * ficha: com uma aberta por cima de outra, cada quadro acha o seu.
   */
  get boardHeaderId(): string {
    return `board-header-${this.$.uid}`
  }

  /** O lugar do "Adicionar" do quadro na linha das abas, no telefone (ver o `add-to` do quadro). */
  get boardAddId(): string {
    return `board-add-${this.$.uid}`
  }

  /** O mostrar encerrados: só na Lista — no quadro quem esconde os terminados é o prazo de cada raia. */
  showsClosedToggle(section: InstanceSection): boolean {
    return !!section.property && this.viewOf(section) === 'list' && this.isThereClosedItems(section)
  }

  get sectionTabs(): ViewTab[] {
    return this.sections.map(section => ({value: section.key || OWN_TAB, title: section.path}))
  }

  /** A seção que a aba mostra. */
  shownSection(tab: string): InstanceSection {
    return this.sections.find(section => (section.key || OWN_TAB) === tab) || this.sections[0]
  }


  /**
   * As duas abas do bloco. Sem `to`: aqui não se navega, alterna-se o que o bloco desenha —
   * é o que distingue esta faixa da da coleção, que usa o mesmo componente.
   */
  get viewTabs(): ViewTab[] {
    return [
      {value: 'list', icon: 'mdi-view-list', title: this.$tt('list') as string},
      {value: 'kanban', icon: 'mdi-view-dashboard-outline', title: this.$tt('kanban') as string}
    ]
  }

  /**
   * A página do quadro desta propriedade: a URL que se guarda e se compartilha.
   *
   * Recebe a SEÇÃO, e não a propriedade: só se chega aqui pelo `hasKanban`, que já exigiu a
   * propriedade — e pedi-la solta obrigaria cada chamada no template a repetir a guarda.
   */
  relationshipRoute(section: InstanceSection) {
    const {system, idea, instance} = this.context
    return {
      name: 'relationship-kanban',
      params: {system, idea, instance: String(instance), property: section.property!.alias}
    }
  }

  /** Em qual aba o bloco deste relacionamento está. Lista é o padrão de quem nunca escolheu. */
  viewOf(section: InstanceSection): RelationshipView {
    return this.sectionViews[section.key] || 'list'
  }

  /**
   * Trocar de aba, e lembrar a escolha.
   *
   * Guardada por PROPRIEDADE, pelo mesmo recorte das preferências do quadro: abrir a ficha de
   * outro item da mesma coleção mostra o relacionamento do mesmo jeito.
   */
  onViewChange(section: InstanceSection, view: RelationshipView) {
    this.sectionViews[section.key] = view
    //Voltando para a Lista depois de mover algo: é aqui que o identificador composto pelo
    //servidor se atualiza — ver o onBoardChanged.
    if (view === 'list' && this.boardChanged) {
      this.boardChanged = false
      this.load()
    }
    if (!section.property) return
    this.$storage.setRelationshipView(this.context.system, this.idea.alias,
        relationshipScope(this.idea.alias, section.property.alias), view)
  }

  /**
   * Um card se moveu no quadro embutido.
   *
   * **Não relê a ficha.** Os itens do quadro SÃO os do `_rels` desta tela, e o quadro já os
   * alterou no lugar — a tabela da aba Lista mostra o valor novo sem ida ao servidor. Antes cada
   * arrasto disparava a releitura inteira do item, que é a requisição mais cara da tela.
   *
   * Fica a marca para a volta: o `_identifier` é composto pelo SERVIDOR, e mover por uma dimensão
   * que faz parte da chave o deixa velho. Reler ao voltar para a Lista custa uma requisição por
   * ida ao quadro, e não uma por card movido.
   */
  onBoardChanged() {
    this.boardChanged = true
    //Já aplicado no lugar: sem isto quem hospeda remontava a ficha, e a releitura que este método
    //existe para evitar acontecia do mesmo jeito, um nível acima
    this.$emit('changed', IN_PLACE)
  }

  /**
   * A seção oferece o "Adicionar" — criar já vinculado, ou vincular existentes (ver o
   * `RelationshipAdd`)? Só no relacionamento MÚLTIPLO (a seção-tabela). E só com a leitura
   * completa: na prévia, os vinculados e os candidatos ainda não chegaram, e a escolha ofereceria
   * o que já está.
   */
  addsTo(section: InstanceSection): boolean {
    return !!section.property && !!section.table && this.complete
  }

  /**
   * As props do "Adicionar" da Lista. No Kanban quem o põe é o próprio quadro (`RelationshipBoard`),
   * que é o mesmo na página expandida.
   */
  addProps(section: InstanceSection) {
    return {
      ownerContext: this.context,
      owner: this.instance,
      property: section.property,
      domains: this.relDomains,
      reverseInfos: this.reverseInfo,
      workspace: this.workspace,
      targetRead: this.candidatesOf(section)
    }
  }

  /**
   * A Lista da seção oferece o X de desvincular em cada linha? A mesma seção que adiciona, e quem pode
   * pela regra do `relationshipEditing` — a mesma do X dos cards no quadro.
   */
  unlinksFrom(section: InstanceSection): boolean {
    const info = this.reverseInfo && section.property ? this.reverseInfo[section.property.alias] : null
    return this.addsTo(section) && relationshipEditing(this.workspace, this.instance, section.property!, info).unlink
  }

  /** O item que o X de uma linha pediu para desvincular, à espera da confirmação. */
  unlinking: { section: InstanceSection, item: Instance } | null = null

  askUnlink(section: InstanceSection, item: Instance) {
    this.unlinking = {section, item}
  }

  /** Desvinculado: sai da seção, no item carregado. */
  onUnlinked(id: number) {
    const section = this.unlinking!.section
    this.unlinking = null
    this.setRelated(section.property!,
        this.itemsOf(section).filter(item => Number(item._instance) !== id))
  }

  /** Entraram itens na seção, inteiros — ver o `RelationshipAdd`. */
  onRelationshipAdded(section: InstanceSection, items: Instance[]) {
    this.setRelated(section.property!, [...this.itemsOf(section), ...items])
  }

  private setRelated(property: IdeaProperty, items: Instance[]) {
    this.onOwnerChanged(withRelated(this.instance, property, items))
  }

  /**
   * O item excluído numa ficha aberta por cima sai das listas desta — dos relacionamentos com a
   * coleção dele —, sem reler: o mesmo que o quadro faz com o card (pedido do usuário). Relia a ficha
   * inteira, e ela piscava para mostrar a lista com um item a menos, o que já estava na mão. Pela
   * coleção, e não só pelo id, porque o id é de cada coleção.
   */
  removeDeleted(idea: string, id: number | string) {
    this.idea.properties.filter(property => property.relationship?.idea === idea).forEach(property => {
      const items = asArray(this.instance._rels && this.instance._rels[property.alias]) as Instance[]
      if (items.some(item => Number(item._instance) === Number(id)))
        this.setRelated(property, items.filter(item => Number(item._instance) !== Number(id)))
    })
  }

  /**
   * O item com um relacionamento mudado — pela Lista, ou devolvido pelo quadro (`update:owner`) —, e
   * avisa quem hospeda que a mudança já está aplicada (`IN_PLACE`): sem remontar, que é reler a ficha
   * inteira para mostrar o que já está na mão (relato do usuário). A seção, a tabela e o quadro
   * embutido leem daqui.
   */
  onOwnerChanged(owner: Instance) {
    this.instance = owner
    this.$emit('changed', IN_PLACE)
  }

  /**
   * Recarregar o dono, para o vínculo pós-criação do múltiplo direto.
   *
   * Método, e não campo com arrow: viaja como prop, e o Vue 3 entrega os métodos já ligados à
   * instância — num campo, o `this` seria o da instância crua da classe.
   */
  async reloadOwner() {
    await this.load()
  }

  /** Há item em situação final na seção — é o que faz a caixa "mostrar encerrados" aparecer. */
  isThereClosedItems(section: InstanceSection) {
    const workflow = this.relationshipWorkflow(section.property!)
    return !!workflow && this.itemsOf(section).some(item => this.isClosed(workflow, item))
  }

  private isClosed(workflow: any, item: Instance): boolean {
    return workflow.isFinal(item)
  }

  /**
   * As colunas da tabela de um relacionamento.
   *
   * Fora as reversões, dos dois lados: numa tabela de canções dentro do compositor, a coluna
   * "Compositor" repetiria em toda linha o item que a ficha já mostra no título.
   *
   * **O relacionamento do relacionamento entra.** A leitura pede `depth: 2`, então os itens de uma
   * seção chegam com os relacionamentos deles resolvidos, e o Gênero de cada canção tem o que
   * mostrar. Seção de terceiro nível não há mais (ver `instanceSections`).
   */
  relProperties(section: InstanceSection) {
    return this.relationshipIdea(section.property!)?.properties.filter(relProperty =>
        !this.isReversion(relProperty))
  }

  isReversion(relProperty: IdeaProperty) {
    return (relProperty.relationship?.reverse && this.idea.properties.find(p => p.alias === relProperty.relationship?.reverse)) ||
        (relProperty.relationship?.idea === this.idea.alias && this.idea.properties.find(p => p.relationship?.reverse === relProperty.alias))
  }

  get currentState() {
    return this.currentStateOf(this.instance)
  }

  /**
   * Em que seções a ficha se divide. A ordem e o achatamento moram no utilitário, que
   * é onde a regra vale para a instância e para os relacionamentos dela por igual.
   */
  get sections(): InstanceSection[] {
    return instanceSections(
        this.idea, this.instance,
        alias => this.workspace.ideaWith(alias),
        //O mesmo resolvedor que o diagrama de classes usa: é ele que deixa o
        //effectiveCardinality achar o outro lado de um relacionamento espelhado
        workspaceNames(this.workspace))
        //O markdown que virou seção obedece a quem vê o campo, como os campos no InstanceFields —
        //decidido aqui pelo mesmo motivo de lá: é por item
        .filter(section => !section.markdown || fieldVisible(section.instance, section.markdown))
  }

  /** As linhas de uma seção-tabela, sem os encerrados quando a caixa está desmarcada. */
  rowsOf(section: InstanceSection): Instance[] {
    const items = this.itemsOf(section)
    const workflow = this.relationshipWorkflow(section.property!)
    if (!workflow || this.isShowClosedItems[section.key]) return items
    return items.filter(item => !this.isClosed(workflow, item))
  }

  /**
   * O que esta ficha leu, na forma da resposta do servidor — o que a janela entrega à página ao
   * expandir. O item como está agora, e não como chegou: uma mudança feita aqui já está nele.
   *
   * Ainda na prévia, devolve a PRÉVIA, com o que falta (`pending`): a página a mostra e continua a
   * leitura que esta ficha não terminou. Entregue como leitura completa, ela nunca releria — e ficaria
   * para sempre com os relacionamentos resumidos.
   */
  asRead(): ViewRead | Preview {
    const read = {value: this.instance, relDomains: this.relDomains, reverseInfo: this.reverseInfo}
    return this.provisional ? {...read, pending: this.pending} : read
  }

  /**
   * O que esta ficha leu da coleção, oferecido a quem criar nela — o "Criar" da faixa do caminho,
   * na ficha, cria outro item da mesma coleção, e com isto a janela abre sem ir ao servidor. Ver o
   * `offerKnown`.
   *
   * Só com a leitura COMPLETA: antes dela os candidatos são o `{}` do começo ou o que a prévia não
   * trouxe, e oferecer isso diria "não há candidato nenhum" — o formulário acreditaria.
   */
  private stopOffering: (() => void) | null = null

  created() {
    this.stopOffering = offerKnown(this.context.idea, () => this.loaded && this.complete
        ? knownForCreate(this.workspace, this.context.idea, this.relDomains, this.reverseInfo) : null)
  }

  beforeUnmount() {
    this.stopOffering?.()
  }

  /**
   * Editar não abre janela: pede à JANELA que troque o que mostra.
   *
   * O evento leva junto tudo o que o editor precisa — coleção, cópia editável, candidatos dos
   * relacionamentos e metadados dos reversos. É exatamente o que esta ficha passava ao modal de
   * edição quando ela mesma o abria; agora sobe um nível, e a janela não precisa reler nada do
   * servidor para mostrar o formulário.
   *
   * Quem hospeda decide: a rota navega para /edit, o modal troca de modo.
   */
  onEdit() {
    this.$emit('edit', editingFrom(this.context, this.asRead(), this.workspace))
  }
}

export default toNative(InstanceView)
</script>

<template>
  <!-- Na rota, no telefone, sem a folga da página: o card vai de borda a borda e o respiro é o dele
       (ver InstanceCard). No modal a folga é a do diálogo, e fica como está. -->
  <ontos-base v-bind="baseProps" ref="base" :phone-flush="framed">
    <template v-if="loaded">
      <!-- Na rota, um card com a régua do item; no modal, uma div — lá o card do diálogo já é a
           moldura, e um card dentro do card seria moldura dupla. A mesma peça que o formulário
           usa, para ver e editar o mesmo item não divergirem de aparência. -->
      <!-- `v-smooth-height` na ficha inteira, e não só nas seções: o que chega com a leitura completa
           fora delas — o rodapé de autoria — fazia o card crescer num salto (relato do usuário, ao
           voltar da edição). Quando são as seções que deslizam, o card só as acompanha. -->
      <component :is="framed ? 'ontos-instance-card' : 'div'"
                 v-smooth-height
                 :class="framed ? 'instance-sheet' : ''">
        <!-- O assunto da tela é a instância; a coleção é o tipo dela, e vira o
             subtítulo. Antes era o contrário, com o identificador numa barra colorida. -->
        <ontos-page-header v-bind="heading">
          <!-- As ações sobre o item, num bloco só (ver o PageHeader) -->
          <template #actions>
            <!-- Trocar a situação não mora aqui: é o selo dela, logo abaixo (`StateSeal`) -->
            <ontos-header-action v-if="complete && canEditItem(instance)" icon="mdi-pencil" :label="$tt('edit')"
                                 color="primary" @click="onEdit"/>
            <ontos-header-action v-if="complete && canDeleteItem(instance)" icon="mdi-delete" :label="$tt('delete')"
                                 color="error" @click="isOnDelete = true"/>
            <!-- O Design não mora mais aqui: está na faixa do caminho e na barra do app (ver o App) -->
          </template>
          <!--Os controles da janela, que só o host conhece, fora do bloco: fechar/página inteira no
              modal-->
          <template v-if="$slots.controls" #controls>
            <slot name="controls" :idea="idea"/>
          </template>
        </ontos-page-header>

        <!-- A situação logo abaixo do título, onde o olho chega primeiro — e é por ela que se troca -->
        <ontos-state-seal v-if="workflow" class="mb-4" :state="workflow.coloredStateOf(instance)"
                          :since="instance._stateChangedAt || ''" :changeable="complete && canEditItem(instance)"
                          @change="isOnStateChange = true"/>

        <!-- As seções da ficha em abas, com mais de uma (pedido do usuário) — o arranjo dos designs, pelo
             mesmo TabbedSection: as abas na linha do título e o botão principal da aba aberta à direita.
             Uma aba por seção do instanceSections: a do item primeiro, com todos os campos, os
             markdowns longos dele, depois um por relacionamento. Sem subabas: o relacionamento do
             relacionado é campo, na aba dele. Com uma seção só, sem abas: só o conteúdo.

             Fora das abas, acima e abaixo: o cabeçalho, o selo da situação e o rodapé de autoria — são
             do item inteiro, e não de uma parte dele. -->
        <!-- `v-smooth-height`: a prévia mostra a seção de relacionamento com um carregando no lugar
             da tabela, e quando a leitura chega a tabela entra de uma vez — na página e na janela
             empurrava tudo o que vem embaixo num salto. Assim a seção cresce deslizando, e o resto
             desce junto. -->
        <ontos-tabbed-section v-smooth-height :tabs="sectionTabs" :opens="sectionTabs[0].value" :routed="framed"
                              plain-controls>
          <!-- O botão principal da aba aberta: o "Adicionar" do relacionamento que ela mostra — o
               mesmo lugar e o mesmo desenho do "Criar coleção" do design (decisão do usuário) -->
          <template #controls="{ tab }">
            <template v-for="section of [shownSection(tab)]" :key="section.key">
              <!-- No quadro, o "Adicionar" é dele, na linha dos botões dele — menos no telefone, onde
                   ele vem para cá (`add-to`) e o resto dos botões do quadro desce -->
              <ontos-relationship-add v-if="addsTo(section) && viewOf(section) !== 'kanban'" v-bind="addProps(section)"
                                      @added="onRelationshipAdded(section, $event)"
                                      @showMessage="$emit('showMessage', $event)"
                                      @showErrorMessage="$emit('showErrorMessage', $event)"/>
              <span v-else-if="addsTo(section) && $vuetify.display.xs" :id="boardAddId"/>
            </template>
          </template>
          <!-- Os controles da visão do relacionamento, na linha das abas (pedido do usuário): a Lista/
               Kanban, também no quadro — trocar de visão não muda o botão de lugar —, e o mostrar
               encerrados, só na Lista: no quadro quem esconde os terminados é o prazo de cada raia.
               O nome da coleção, que era o subtítulo da seção, saiu: a aba já diz qual é o
               relacionamento, e a primeira coluna da tabela repetia o nome (relato do usuário). -->
          <template #tools="{ tab }">
            <template v-for="section of [shownSection(tab)]" :key="section.key">
              <!-- No quadro, o lugar dos botões dele — adicionar, campos, situação, dimensões,
                   expandir: o quadro os desenha aqui (`header-to`), e não numa linha acima das raias.
                   No telefone, os dois vão para a linha de baixo (ver .section-tools-below). -->
              <template v-if="!$vuetify.display.xs">
                <span v-if="hasKanban(section) && viewOf(section) === 'kanban'" :id="boardHeaderId"/>
                <ontos-show-closed-toggle v-if="showsClosedToggle(section)" v-model="isShowClosedItems[section.key]"/>
              </template>
              <!-- A troca de visão por último, na ponta direita (pedido do usuário) -->
              <ontos-view-tabs v-if="hasKanban(section)" :views="viewTabs" :model-value="viewOf(section)"
                               @update:model-value="onViewChange(section, $event)"/>
            </template>
          </template>
          <template #default="{ tab }">
          <template v-for="section of [shownSection(tab)]" :key="section.key">
          <!-- No telefone, na linha das abas só o "Adicionar" e a Lista/Kanban; o resto — os botões do
               quadro, o mostrar encerrados — numa linha logo abaixo (pedido do usuário) -->
          <div v-if="$vuetify.display.xs && (viewOf(section) === 'kanban' && hasKanban(section) || showsClosedToggle(section))"
               class="section-tools-below">
            <span v-if="hasKanban(section) && viewOf(section) === 'kanban'" :id="boardHeaderId"/>
            <ontos-show-closed-toggle v-if="showsClosedToggle(section)" v-model="isShowClosedItems[section.key]"/>
          </div>
          <!-- As falhas do relacionamento logo abaixo das abas — o mesmo lugar na Lista e no quadro
               (o #below dele, abaixo). Longe dela ficavam longe do que acusam (relatos do usuário). -->
          <ontos-field-failures v-if="sectionFailures(section).length"
                                :failures="sectionFailures(section)" class="mb-2"/>

          <!-- A prévia traz o relacionamento só resumido (id e identificador): o espaço fica reservado até
               a leitura completa, e não uma tabela de células em branco que pareceria dado ausente. -->
          <template v-if="!isPending(section)">
          <!-- Relacionamento múltiplo: a tabela de sempre, com a linha clicável -->
          <!-- hover: no Vuetify 2 o realce da linha vinha do CSS de tema e valia para
               toda tabela; no 4 virou prop, e sem ela a linha clicável não responde -->
          <!-- A tabela quando as colunas cabem; senão, a visão compacta (ver TableFit), a mesma lista
               da grade da coleção (ver InstanceListRow) — rolar para o lado, não (regra do usuário) -->
          <ontos-table-fit v-if="section.table && viewOf(section) === 'list' && rowsOf(section).length"
                           v-slot="{ compact }">
          <div v-if="compact">
            <ontos-instance-list-row v-for="item in rowsOf(section)" :key="item._instance" class="compact-list-row"
                                     :instance="item" :idea="section.idea" :fields="relRowFields(section, item)"
                                     :state="relRowState(section, item)" :context="rowContext(section, item)"
                                     :choice-domains="workspace.domains" @click.stop="openRow(section, item)">
              <template v-if="unlinksFrom(section)" #actions>
                <v-btn variant="text" icon size="x-small" class="unlink-btn"
                       :title="$tt('relationship-unlink')" @click.stop="askUnlink(section, item)">
                  <v-icon size="x-small">mdi-close</v-icon>
                </v-btn>
              </template>
            </ontos-instance-list-row>
          </div>
          <v-table v-else
                   class="custom-table ontos-list-table" hover>
            <template v-slot:default>
              <thead>
              <tr>
                <!-- Coluna só do acento: sem título, porque ela não tem assunto — é a marca do
                     item, a mesma da linha na listagem e do topo do card. -->
                <th class="accent-col"></th>
                <!-- O identificador como primeira coluna, com o nome da coleção por título.
                     É redundante — ele é composto das colunas seguintes —, e ainda assim ganha
                     lugar: em toda outra tela a régua vem colada ao NOME do item, e sem ele a
                     linha tinha a marca da cor sem dizer de quem ela é.

                     Só quando a chave é COMPOSTA: com um campo só, esta coluna sairia idêntica
                     à vizinha. -->
                <th v-if="hasCompositeIdentifier(section.idea)" class="text-left">
                  {{ section.idea.name }}
                </th>
                <th class="text-left" v-for="relProperty in relProperties(section)"
                    :key="`${section.key}.${relProperty.alias}`">
                  {{ relProperty.name }}
                </th>
                <th class="text-left" v-if="section.idea.workflow">
                  {{ $tt('state') }}
                </th>
                <!-- Coluna só do X, sem título, como a do acento: não tem assunto -->
                <th v-if="unlinksFrom(section)" class="unlink-col"></th>
              </tr>
              </thead>
              <tbody>
              <!-- O `true` do openInstance: a linha de uma tabela é um item de LISTA, e dali se
                   espia e se volta — então a ficha abre POR CIMA, e fechá-la devolve esta tabela.
                   É o mesmo que o card do Kanban embutido faz, e o contrário do chip, que troca a
                   janela por ser navegação dentro do item. -->
              <tr v-for="item in rowsOf(section)" :key="item._instance" class="clickable-row ontos-list-row"
                  @click.stop="openRow(section, item)">
                <td class="accent-col">
                  <ontos-color-accent row :color="instanceColorOf(item)"/>
                </td>
                <td v-if="hasCompositeIdentifier(section.idea)" class="ontos-list-name">
                  <ontos-instance-identifier :instance="item" :idea="section.idea"/>
                </td>
                <!-- O nome do item em destaque (ver .ontos-list-table): a coluna da chave, quando ela
                     é um campo só; a composta tem a coluna própria, logo acima -->
                <td class="first-col" v-for="relProperty in relProperties(section)"
                    :class="{'ontos-list-name': relProperty.identifier && !hasCompositeIdentifier(section.idea)}"
                    :key="`value.${section.key}.${relProperty.alias}`">
                  <template v-for="(simpleValue, i) in propertyValueArrayGeneric(item, relProperty)"
                            :key="`${section.key}.${relProperty.alias}[${i}]`">
                    <ontos-property-value
                        :property="relProperty"
                        :value="simpleValue"
                        :item="item"
                        :context="rowContext(section, item)"
                        :choice-domains="workspace.domains"
                        variant="cell"
                        @preview="imagePreview = $event"
                        @link="onInstanceLink($event.event, $event.context)"/>
                  </template>
                </td>
                <!-- O semáforo com o nome, pelo mesmo componente da grade da coleção -->
                <td class="second-col" v-if="relRowState(section, item)">
                  <ontos-colored-option :option="relRowState(section, item)!" dot/>
                </td>
                <!-- Desvincular, no fim da linha: pequeno e apagado, como o X de um campo de
                     relacionamento (escolha do usuário). `.stop`, porque o clique da linha abre o
                     item. Quem pergunta e grava é a ficha — ver o `askUnlink`. -->
                <td v-if="unlinksFrom(section)" class="unlink-col">
                  <v-btn variant="text" icon size="x-small" class="unlink-btn"
                         :title="$tt('relationship-unlink')" @click.stop="askUnlink(section, item)">
                    <v-icon size="x-small">mdi-close</v-icon>
                  </v-btn>
                </td>
              </tr>
              </tbody>
            </template>
          </v-table>
          </ontos-table-fit>
          <!-- Os relacionados desta seção que quem olha não vê: contados, sem dizer quais -->
          <div v-if="section.property && hiddenCount(instance, section.property)" class="px-2 py-1">
            <ontos-hidden-count :count="hiddenCount(instance, section.property)"/>
          </div>

          <!-- O quadro desta propriedade, dentro do bloco. Montado só na aba: uma ficha com
               vários relacionamentos dispararia um carregamento por quadro ao abrir.

               O nome da propriedade está na aba; no #title do quadro, só o subtítulo (a coleção),
               ao lado das ações dele, que é o que ele tem de próprio. -->
          <ontos-relationship-board v-if="hasKanban(section) && viewOf(section) === 'kanban'"
                                    flush
                                    :header-to="`#${boardHeaderId}`"
                                    :add-to="$vuetify.display.xs ? `#${boardAddId}` : ''"
                                    :owner-context="context"
                                    :owner="instance"
                                    :property="section.property"
                                    :reverse-info="reverseInfo"
                                    :workspace="workspace"
                                    :reload-owner="reloadOwner"
                                    :domains="relDomains"
                                    :editable="addsTo(section)"
                                    :known-candidates="candidatesOf(section)"
                                    @update:owner="onOwnerChanged($event)"
                                    @candidates="rememberCandidates(section, $event)"
                                    @open-instance="openBoardItem"
                                    @changed="onBoardChanged()"
                                    @no-dimensions="onViewChange(section, 'list')"
                                    @showMessage="$emit('showMessage', $event)"
                                    @showErrorMessage="$emit('showErrorMessage', $event)">
            <!-- Sem título: o nome da seção já está na aba, e repeti-lo seria eco. O slot vazio, e não
                 ausente, porque sem ele o quadro põe o nome da coleção no lugar. Sem as visões também:
                 a Lista/Kanban está na linha das abas (o #tools acima), e os botões do quadro vão
                 para lá (`header-to`). As falhas, logo abaixo das abas, como na Lista. -->
            <template #title>
              <span/>
            </template>
            <!-- O "Adicionar", o "+" de cada raia e o X dos cards são do próprio quadro: ele os põe
                 aqui e na página expandida, que é o mesmo quadro -->
            <template #controls>
              <!-- Só na aba do quadro, porque é o que ele faz: leva o MESMO quadro para a
                   página inteira, na URL que se guarda e se compartilha. Na Lista não teria o
                   que expandir. Controle da janela, fora do bloco das ações do quadro. -->
              <ontos-header-action icon="mdi-open-in-new" :label="$tt('expand')"
                                   :to="relationshipRoute(section)"/>
            </template>
          </ontos-relationship-board>

          <!-- O markdown de mais de uma linha, na aba dele: o texto desenhado, sem borda, integrado à
               página (pedido do usuário) — o nome do campo é o da aba. Nos campos ele também está,
               linearizado (decisão do usuário: as duas formas) -->
          <div v-if="section.markdown" class="ontos-inset mb-3">
            <!-- O visualizador direto, e não o PropertyValue: na ficha ele dá o linearizado com o ⤢,
                 que é a forma do campo; aqui é o texto inteiro, desenhado -->
            <markdown-viewer v-for="(value, index) in asArray(section.instance[section.markdown.alias])"
                             :key="index" :initialValue="shownMarkdown(value, workspace, section.instance, context.idea)"/>
          </div>

          <!-- Cardinalidade 1: a linha do item antes dos campos dele, na margem deles (decisão do
               usuário) -->
          <ontos-related-item-line v-if="section.property && !section.table"
                                   :property="section.property" :item="section.instance" :context="context"
                                   @link="onInstanceLink($event.event, $event.context)"/>

          <!-- Cardinalidade 1 e a própria instância: o mesmo bloco de campos.

               `v-if` solto, e não `v-else-if`: a linha do item entra entre este bloco e a
               tabela, e uma cadeia quebrada aqui faria os campos sumirem justamente na seção
               de cardinalidade 1. A condição basta sozinha — quando não é tabela, a tabela
               acima já não desenha. -->
          <ontos-instance-fields v-if="!section.table && !section.markdown"
                                 :properties="section.properties"
                                 :instance="section.instance"
                                 :context="context"
                                 :choice-domains="workspace.domains"
                                 @preview="imagePreview = $event"
                                 @link="onInstanceLink($event.event, $event.context)"/>
          </template>
          <ontos-dialog-loading v-else min-height="96px"/>
          </template>
          </template>
        </ontos-tabbed-section>

        <!-- O rodapé: quem criou e quem alterou, e há quanto tempo — miúdo e embaixo (pedido do
             usuário), porque é o que se consulta de vez em quando. A data exata ao passar o mouse.
             Com a cara e o nome, e não o e-mail, que o modo de uso não recebe. -->
        <footer v-if="complete && authorship.length"
                class="instance-authorship ontos-arrives text-caption text-medium-emphasis">
          <span v-for="author of authorship" :key="author.key" class="authorship-entry" :title="author.exact">
            {{ author.label }}
            <template v-if="author.person">
              <ontos-user-avatar :user="author.person.user" :label="personName(author.person)"
                                 :photo="author.person.photo" :size="18"/>
              <span class="authorship-name">{{ personName(author.person) }}</span>
            </template>
            <span v-if="author.when">{{ author.when }}</span>
          </span>
        </footer>
      </component>

      <!-- Junto dos demais diálogos da ficha, e não solto: é nesta árvore que o
           Vuetify acha os aninhados quando a ficha já está num modal. -->
      <!-- `&& workflow` não é defensivo à toa: o modal só abre por um botão que já exige
           fluxo, mas agora a lista de opções sai DELE, e sem a guarda o template desreferencia
           um opcional. -->
      <!-- O MESMO cabeçalho da ficha por baixo: é a mesma coisa sendo mostrada, e a janela que
           troca a situação não deve se apresentar de outro jeito. O que se troca vai no rótulo
           do conteúdo, pelo `field`. -->
      <ontos-state-dialog v-if="isOnStateChange && workflow" :heading="editHeading"
                          :field="$tt('state')"
                          form="dot"
                          :options="workflow.coloredStates()"
                          :current="currentState.id"
                          @pick="onPickState($event)"
                          @closed="isOnStateChange = false"/>

      <!-- O `true` do open-instance: a ficha do bloqueador abre POR CIMA desta janela,
           para que fechá-la devolva a lista de quem impede a exclusão. -->
      <ontos-delete-instance-dialog v-if="isOnDelete"
                                    :context="context"
                                    :identifier="instance._identifier"
                                    :idea-name="idea.name"
                                    @confirmed="onDeleteConfirmed"
                                    @open-instance="openInstance($event, true)"
                                    @closed="isOnDelete = false"
                                    @showErrorMessage="$emit('showErrorMessage', $event)"/>

      <!-- Desvincular, pelo X de uma linha da Lista: a mesma pergunta e a mesma gravação do X de um
           card, que o quadro tem consigo -->
      <ontos-relationship-unlink-dialog v-if="unlinking"
                                        :owner-context="context" :owner="instance"
                                        :property="unlinking.section.property" :item="unlinking.item"
                                        :workspace="workspace"
                                        @unlinked="onUnlinked($event)"
                                        @closed="unlinking = null"
                                        @showMessage="$emit('showMessage', $event)"/>

      <ontos-image-preview-dialog v-if="imagePreview"
                                  :src="imagePreview.src"
                                  :title="imagePreview.title"
                                  @closed="imagePreview = null"/>
    </template>
  </ontos-base>
</template>
<style scoped>
/* O respiro de dentro do card. Na página a ficha não tinha moldura e encostava na margem do
   conteúdo; dentro do card ela precisa do próprio.

   O respiro é PUBLICADO na variável porque as réguas das seções o furam para ir de borda a
   borda — ver o modo flush do Section. Escrito em dois lugares, mudar um e esquecer o outro
   deixaria a régua curta ou passando da borda. */
.instance-sheet {
  --ontos-section-bleed: 20px;
  padding: var(--ontos-section-bleed);
}

/* No telefone, sem a folga da página, este é o único respiro até a borda: os 16px do modal em tela
   cheia. Pela variável, as réguas das seções acompanham */
@media (max-width: 599.98px) {
  .instance-sheet {
    --ontos-section-bleed: 16px;
  }
}

/* O rodapé da autoria: uma linha fina separada do conteúdo por uma régua apagada, com as duas
   entradas lado a lado e quebrando para baixo no telefone.

   No card, a régua vai de borda a borda como as das seções (o modo flush do Section): a margem
   negativa fura o respiro, e o recuo devolve o texto ao alinhamento do conteúdo. Sem isso ela parava
   no respiro, curta ao lado das réguas de cima (relato do usuário). Fora do card a variável não
   existe, e o rodapé fica como está. */
.instance-authorship {
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin: 24px calc(var(--ontos-section-bleed, 0px) * -1) 0;
  padding: 10px var(--ontos-section-bleed, 0px) 0;
}

.authorship-entry {
  align-items: center;
  display: inline-flex;
  gap: 6px;
}

.authorship-name {
  font-weight: 500;
}

.custom-table {
  table-layout: auto;
  width: 100%;
  overflow: visible;
}

.custom-table td {
  white-space: normal;
  word-wrap: break-word;
}

/* Primeira coluna pode quebrar linha */
.custom-table .first-col {
  max-width: 100%;
}

/* Segunda coluna deve permanecer em uma linha só */
.custom-table .second-col {
  white-space: nowrap;
  width: 1%;
}

/* Os itens da visão compacta, divididos como as linhas da tabela que eles substituem */
.compact-list-row {
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.compact-list-row:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

/* A célula do acento é estreita, sem recuo, e serve de referência de posição para a régua —
   o resto da receita é do modo `row` do ColorAccent, o mesmo que a listagem usa. */
.accent-col {
  position: relative;
  width: 4px;
  padding: 0;
}

/* A coluna do X de desvincular: só a largura do botão, no fim da linha (pedido do usuário: sem
   gastar espaço), sem o recuo que a tabela dá a toda célula. */
.custom-table .unlink-col {
  width: 28px;
  padding: 0 2px;
  text-align: right;
}

/* Apagado, ganha contraste ao se aproximar — o mesmo desenho do X de um campo de relacionamento */
/* A linha de baixo das abas, no telefone: os controles à direita, como estariam na de cima */
.section-tools-below {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 8px;
}

.unlink-btn {
  opacity: .5;
}

.unlink-btn:hover {
  opacity: 1;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

/* .url-value mudou-se para o PropertyValue.vue junto com a marcação que a usava. O markdown na ficha não tem mais borda (pedido do usuário): o de várias linhas é uma
   seção, e o texto se integra à página. */

/*
:deep(.toastui-editor-contents *) {
  margin: 0.2em 0;
  padding-top: 0;
  padding-bottom: 0;
}
*/
</style>
